import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../utils/api";
import { setPageTitle } from "../utils/setPageTitle";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const MaterialsList = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // FIX: the backend (materials.js) restricts create/update/delete/count
  // to authorize("admin") only. The UI previously showed those controls
  // to every role, so a staff/engineer/storekeeper user could click
  // Add/Edit/Delete and get a confusing generic failure from the 403.
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    category: "",
    unit: "pcs",
    unit_price: "",
  });
  const [saving, setSaving] = useState(false);

  // Fetch all materials
  const fetchMaterials = async () => {
    try {
      const res = await api.get("/api/materials");
      setMaterials(res.data);
    } catch (err) {
      console.error("Error fetching materials:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageTitle("Materials Management");
    fetchMaterials();
  }, []);

  // Handle open dialog
  const handleOpen = (material = null) => {
    if (material) {
      setFormData(material);
    } else {
      setFormData({ id: null, name: "", category: "", unit: "pcs", unit_price: "" });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Add or update material
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert("Material name is required.");
      return;
    }
    // FIX: unit_price had no validation at all — a negative price could
    // be saved and would silently corrupt any quotation total it's used in.
    const price = Number(formData.unit_price);
    if (!Number.isFinite(price) || price < 0) {
      alert("Unit price must be a valid number of 0 or more.");
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData, unit_price: price };
      if (formData.id) {
        await api.put(`/api/materials/${formData.id}`, payload);
      } else {
        await api.post("/api/materials", payload);
      }
      await fetchMaterials();
      setOpen(false);
    } catch (err) {
      console.error("Error saving material:", err);
      alert(err?.response?.data?.error || "Failed to save material.");
    } finally {
      setSaving(false);
    }
  };

  // Delete material
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this material?")) return;
    try {
      await api.delete(`/api/materials/${id}`);
      setMaterials((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error("Error deleting material:", err);
      alert(err?.response?.data?.error || "Failed to delete material.");
    }
  };

  // Columns for DataGrid
  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "name", headerName: "Material Name", flex: 1 },
    { field: "category", headerName: "Category", flex: 1 },
    { field: "unit", headerName: "Unit", width: 100 },
    {
      field: "unit_price",
      headerName: "Unit Price (₦)",
      width: 150,
      renderCell: (params) => `₦ ${Number(params.value).toLocaleString()}`,
    },
    {
      field: "last_updated",
      headerName: "Last Updated (UTC)",
      flex: 1,
      // FIX: MUI X v6+ changed valueFormatter's signature — it now
      // receives the raw cell value directly as its first argument,
      // not a `params` object with a `.value` property (that was the
      // v5 API). Reading `params.value` here was always undefined,
      // which is why every row showed "—" regardless of the real date
      // the backend was sending back correctly.
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleString("en-GB", { timeZone: "UTC" }) : "—",
    },
    // Actions column only exists for admins — matches the backend's
    // authorize("admin") gate on POST/PUT/DELETE.
    ...(isAdmin
      ? [
          {
            field: "actions",
            headerName: "Actions",
            width: 130,
            renderCell: (params) => (
              <>
                <IconButton onClick={() => handleOpen(params.row)} color="primary" size="small">
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(params.row.id)} color="error" size="small">
                  <DeleteIcon />
                </IconButton>
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* ======= Top Header Section ======= */}
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 3,
            backgroundColor: "#1976d2",
            color: "#fff",
            p: 2,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            🧱 Materials Management
          </Typography>

          <Box>
            <Button
              variant="outlined"
              sx={{ mr: 2, color: "white", borderColor: "white" }}
              onClick={() => navigate(-1)}
            >
              ← Back
            </Button>
            <Button
              variant="outlined"
              sx={{ mr: 2, color: "white", borderColor: "white" }}
              onClick={logout}
            >
              Logout
            </Button>
            {/* FIX: only admins can actually create a material on the
                backend — hide the button for everyone else instead of
                letting them hit a 403 after filling the form. */}
            {isAdmin && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => handleOpen()}
              >
                Add Material
              </Button>
            )}
          </Box>
        </Grid>

        {/* ======= Data Table ======= */}
        {loading ? (
          <CircularProgress />
        ) : (
          <Box
            sx={{
              height: 900,
              backgroundColor: "#fafafa",
              borderRadius: 3,
              boxShadow: 3,
              p: 2,
            }}
          >
            <DataGrid
              rows={materials}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                pagination: { paginationModel: { pageSize: 15 } },
              }}
              pageSizeOptions={[8, 15, 30]}
              disableRowSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#e3f2fd",
                },
              }}
            />
          </Box>
        )}

        {/* ======= Add/Edit Material Dialog (admin only) ======= */}
        {isAdmin && (
          <Dialog open={open} onClose={handleClose}>
            <DialogTitle sx={{ backgroundColor: "#1976d2", color: "white" }}>
              {formData.id ? "Edit Material" : "Add New Material"}
            </DialogTitle>
            <DialogContent sx={{ mt: 1 }}>
              <TextField
                margin="dense"
                name="name"
                label="Material Name"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="category"
                label="Category"
                fullWidth
                variant="outlined"
                value={formData.category}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="unit"
                label="Unit"
                fullWidth
                variant="outlined"
                value={formData.unit}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="unit_price"
                label="Unit Price (₦)"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.unit_price}
                onChange={handleChange}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </DialogContent>
            <DialogActions sx={{ pr: 3, pb: 2 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSave} variant="contained" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Box>
  );
};

export default MaterialsList;
